import { useState, useMemo, useEffect } from 'react';
import { Button, Space, Switch, message, Empty } from 'antd';
import { ReloadOutlined, FileExcelOutlined, TableOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import PivotTableUI from 'react-pivottable/PivotTableUI';
import type { PivotTableUIProps } from 'react-pivottable/PivotTableUI';
import 'react-pivottable/pivottable.css';
import { DataGrid, type DataGridColumn } from './yeni-design/DataGrid';
import { TierBadge } from './TierBadge';
import { DetailPanel } from './DetailPanel';
import { formatValue, inferColumnsFromData, mapToDataGridColumns } from './columnUtils';
import { ParameterFormBar, computeDefaults, type ParamValues } from './ParameterFormBar';
import { queryDataSource } from '../api/query';
import type { ReportConfig } from '../types/panelConfig';

type ReportRow = Record<string, unknown>;

const PARAM_STORAGE_PREFIX = 'report-params:';

function loadStoredParams(reportCode: string): ParamValues | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(`${PARAM_STORAGE_PREFIX}${reportCode}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      // shallow string-coerce
      const out: ParamValues = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === 'string') out[k] = v;
      }
      return out;
    }
  } catch {
    /* corrupt storage value — ignore */
  }
  return null;
}

function persistParams(reportCode: string, values: ParamValues) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      `${PARAM_STORAGE_PREFIX}${reportCode}`,
      JSON.stringify(values),
    );
  } catch {
    /* quota exceeded or disabled — best-effort */
  }
}

async function exportToExcel(report: ReportConfig, rows: Record<string, unknown>[]) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'RaporGor';
  wb.created = new Date();
  const ws = wb.addWorksheet(report.name.slice(0, 31));

  ws.columns = report.columns.map((c) => ({
    header: c.header,
    key: c.field,
    width: Math.max(12, Math.round((c.width ?? 120) / 7)),
  }));

  ws.getRow(1).font = { bold: true };
  ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
  ws.getRow(1).alignment = { vertical: 'middle' };

  for (const row of rows) {
    const mapped: Record<string, unknown> = {};
    for (const c of report.columns) {
      let v = row[c.field];
      if (v != null && c.type === 'date') {
        const d = dayjs(String(v));
        v = d.isValid() ? d.toDate() : v;
      }
      mapped[c.field] = v;
    }
    ws.addRow(mapped);
  }

  report.columns.forEach((c, idx) => {
    const col = ws.getColumn(idx + 1);
    if (c.type === 'currency') col.numFmt = '#,##0.00 "₺"';
    else if (c.type === 'percentage') col.numFmt = '0.00%';
    else if (c.type === 'number') col.numFmt = '#,##0.##';
    else if (c.type === 'date') col.numFmt = c.format === 'dd.MM.yyyy' ? 'dd.MM.yyyy' : 'yyyy-mm-dd';
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${report.code}-${dayjs().format('YYYYMMDD-HHmm')}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface GenericReportPageProps {
  report: ReportConfig;
  allReports: ReportConfig[];
  onNavigateReport?: (code: string, param: string | number) => void;
}

export function GenericReportPage({ report, allReports, onNavigateReport }: GenericReportPageProps) {
  const pivotCapable = report.code === 'satis-karlilik';
  const [pivotMode, setPivotMode] = useState(false);
  const [pivotState, setPivotState] = useState<Partial<PivotTableUIProps>>({});
  const [expandedRow, setExpandedRow] = useState<Record<string, unknown> | null>(null);

  // Smart defaults derived from report.parameters; overridden by localStorage if any.
  // `paramValues` is the form's draft state. `activeValues` is what the query actually
  // uses (only updated on submit) — prevents request fan-out while user types.
  const initialDefaults = useMemo(() => computeDefaults(report.parameters), [report.parameters]);
  const initial = useMemo(() => {
    const stored = loadStoredParams(report.code);
    if (!stored) return initialDefaults;
    // merge: stored values for known keys only; defaults for new keys
    const merged: ParamValues = { ...initialDefaults };
    for (const p of report.parameters) {
      if (Object.prototype.hasOwnProperty.call(stored, p.key)) {
        merged[p.key] = stored[p.key];
      }
    }
    return merged;
  }, [report.code, report.parameters, initialDefaults]);

  const [paramValues, setParamValues] = useState<ParamValues>(initial);
  const [activeValues, setActiveValues] = useState<ParamValues>(initial);

  // Reset both states when report changes (navigation between reports).
  useEffect(() => {
    setParamValues(initial);
    setActiveValues(initial);
  }, [report.code, initial]);

  const handleRowClick = (row: Record<string, unknown>) => {
    if (!report.detailConfig) return;
    setExpandedRow((current) =>
      current && current[report.detailConfig!.linkField] === row[report.detailConfig!.linkField]
        ? null
        : row
    );
  };

  const handleSubmit = () => {
    setActiveValues(paramValues);
    persistParams(report.code, paramValues);
  };

  const handleResetParams = () => {
    setParamValues(initialDefaults);
    setActiveValues(initialDefaults);
    persistParams(report.code, initialDefaults);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['report', report.code, report.sourceId, activeValues],
    queryFn: async () => {
      if (!report.sourceId) return [];
      const res = await queryDataSource({ sourceId: report.sourceId, parameters: activeValues });
      if (!res.success) {
        throw new Error(res.errorMessage ?? 'Sorgu çalıştırılamadı');
      }
      return res.data ?? [];
    },
    enabled: !!report.sourceId,
  });

  const errorMessage = error instanceof Error ? error.message : null;

  // Backend ittihat path emits `columns: []` because sproc result schemas are dynamic.
  // When metadata is empty but data arrived, infer columns from the first row so the
  // table renders with sensible headers + types instead of an empty grid.
  // onNavigateReport is reserved for future drill-down columns (cellRenderer === 'drillDown').
  void onNavigateReport;
  const columns = useMemo<DataGridColumn<ReportRow>[]>(() => {
    if (report.columns.length > 0) {
      return mapToDataGridColumns(report.columns);
    }
    if (data && data.length > 0) {
      return mapToDataGridColumns(inferColumnsFromData(data[0] as Record<string, unknown>));
    }
    return [];
  }, [report.columns, data]);

  const aggregations = useMemo(() => {
    if (!data || data.length === 0) return [];
    return report.columns
      .filter((col) => col.agg)
      .map((col) => {
        const values = data.map((row: Record<string, unknown>) => Number(row[col.field]) || 0);
        let result = 0;
        switch (col.agg) {
          case 'sum': result = values.reduce((a: number, b: number) => a + b, 0); break;
          case 'avg': result = values.reduce((a: number, b: number) => a + b, 0) / values.length; break;
          case 'count': result = values.length; break;
          case 'min': result = Math.min(...values); break;
          case 'max': result = Math.max(...values); break;
        }
        return { label: col.header, value: formatValue(col, result) };
      });
  }, [data, report.columns]);

  const handleExport = async () => {
    try {
      await exportToExcel(report, data ?? []);
      message.success('Excel indirildi');
    } catch (e) {
      console.error(e);
      message.error('Excel oluşturulamadı');
    }
  };

  const rowCount = data?.length ?? 0;

  return (
    <div>
      {/* Başlık satırı */}
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>{report.name}</h1>
          <TierBadge tier={report.tier} size="md" showLabel />
          <span style={styles.subtitle}>{report.category}</span>
        </div>
        <Space wrap size={8}>
          {pivotCapable && (
            <div style={styles.pivotToggle}>
              {pivotMode ? <AppstoreOutlined /> : <TableOutlined />}
              <span style={{ fontSize: 12, fontWeight: 500 }}>Pivot</span>
              <Switch size="small" checked={pivotMode} onChange={setPivotMode} />
            </div>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} size="small">Yenile</Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExport}
            disabled={!data || data.length === 0}
            size="small"
          >
            Excel
          </Button>
        </Space>
      </div>

      {/* Parameter form bar — yeni-design styled. Type-aware inputs (date/number/string/bool).
          Dates default to today; user can override; submit triggers query re-run; values
          persisted to localStorage per-report. Empty params → "Bu rapor için filtre yok"
          chip. */}
      <ParameterFormBar
        parameters={report.parameters}
        values={paramValues}
        onChange={setParamValues}
        onSubmit={handleSubmit}
        onReset={handleResetParams}
        isLoading={isLoading}
        rowCount={rowCount}
      />

      {!report.sourceId && (
        <div style={styles.panel}>
          <Empty description="Bu rapor henüz yapılandırılmamış" style={{ padding: 32 }} />
        </div>
      )}

      {report.sourceId && errorMessage && (
        <div style={styles.panel}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={errorMessage}
            style={{ padding: 32 }}
          >
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} size="small">
              Tekrar dene
            </Button>
          </Empty>
        </div>
      )}

      {report.sourceId && !errorMessage && !pivotMode && (
        <>
          <DataGrid<ReportRow>
            rows={(data ?? []) as ReportRow[]}
            columns={columns}
            loading={isLoading}
            pageSize={50}
            onRowClick={report.detailConfig ? handleRowClick : undefined}
            onExport={handleExport}
            toolbarTitle={`${data?.length ?? 0} kayıt`}
          />

          {report.detailConfig && expandedRow && (
            <DetailPanel
              detailConfig={report.detailConfig}
              masterRow={expandedRow}
              allReports={allReports}
            />
          )}

          {aggregations.length > 0 && (
            <div style={styles.aggBar}>
              {aggregations.map((agg) => (
                <div key={agg.label} style={styles.aggItem}>
                  <span style={styles.aggLabel}>{agg.label}</span>
                  <strong className="tabular" style={styles.aggValue}>{agg.value}</strong>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {report.sourceId && !errorMessage && pivotMode && pivotCapable && (
        <div style={styles.pivotPanel}>
          <PivotTableUI
            data={(data ?? []) as never}
            onChange={(s: PivotTableUIProps) => setPivotState(s)}
            {...pivotState}
          />
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  headerLeft: { display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 11,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    fontWeight: 500,
  },
  pivotToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 10px',
    background: 'var(--color-hover-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    color: 'var(--color-text-secondary)',
  },
  panel: {
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
  },
  gridPanel: {
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    overflow: 'hidden',
  },
  aggBar: {
    marginTop: 10,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    padding: '10px 14px',
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
  },
  aggItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  aggLabel: {
    fontSize: 10,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontWeight: 600,
  },
  aggValue: {
    fontSize: 14,
    color: 'var(--color-text-primary)',
    fontWeight: 700,
  },
  pivotPanel: {
    background: 'var(--color-card-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 4,
    padding: 12,
    overflow: 'auto',
  },
};
